// Rapports › « Coûts IA » (owner, 2026-10-22): what the AI costs, where it goes,
// and whether our ledger matches OpenRouter's bill.
//   • today / 7 jours / mois (fixed windows), then for the page's period:
//   • per day (inline SVG, like the home chart), by feature, by model,
//   • AI per DELIVERED order, the 10 most expensive orders (links),
//   • the daily OpenRouter check (amber over 5 %).
//   • « Fournisseurs IA » (2026-10-23): every figure Réel (the full value,
//     OpenRouter + Claude even what its free credit covers) and Payé (the cash:
//     OpenRouter + Claude not covered); by provider (OpenRouter / Claude);
//     Claude's credit used vs paid; the daily Claude check (amber over 5 %).
//     An older worker sends no payé: the card is exactly as before.
// Data: worker GET /dashboard/ai-costs (lib/aiCostLedger.ts — MAD at
// app_settings.fx_rates.USD). Missing data says so; it is never a 0.
// Night mode: tokens only (dashboard.night.guard.test.ts scans this file).
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { DateRange } from '@/components/admin/DateRangePicker';
import { dashboardApi, type AiCostsResponse, type AiCostRow, type AiClaudeCheck } from '@/lib/dashboardApi';
import { aiFeatureLabel } from '@/lib/aiFeatures';
import { formatNum, formatDecimal } from '@/lib/homeDashboard';
import { useT } from '@/lib/i18n';
import { CARD_BORDER, HEADING_FONT } from './home/homeParts';

const GOLD = '#C9A24A';
const frDate = (d: string | null | undefined) => (d ? d.split('-').reverse().join('/') : '—');
const money = (n: number, lang: string) => (n < 10 ? formatDecimal(Math.round(n * 100) / 100, lang) : formatNum(n));
/** A payé figure worth saying: sent by the worker and different from the réel one. */
const paidDiffers = (mad: number, paid: number | null | undefined): paid is number => paid != null && paid !== mad;
const PROVIDER_LABEL: Record<string, string> = { openrouter: 'OpenRouter', anthropic: 'Claude' };

function Tile({ label, mad, calls, missing, paidMad, lang }: { label: string; mad: number; calls: number; missing: boolean; paidMad?: number; lang: string }) {
  const { t } = useT();
  return (
    <div className={`bg-white rounded-xl border ${CARD_BORDER} px-3 py-2.5 min-w-0`}>
      <div className="text-[11.5px] text-adm-muted font-medium">{label}</div>
      <div className={`text-[22px] leading-tight font-medium ${missing ? 'text-adm-faint' : 'text-adm-ink'}`} style={{ fontFamily: HEADING_FONT }}>
        {missing ? '—' : money(mad, lang)} {!missing && <small className="font-sans text-[12px] text-adm-muted">DH</small>}
      </div>
      <div className="text-[11px] text-adm-muted">{missing ? <span className="text-status-waiting font-semibold">{t('données manquantes')}</span> : t('{n} appels', { n: formatNum(calls) })}</div>
      {!missing && paidDiffers(mad, paidMad) && (
        <div data-ai-paid className="text-[11px] text-adm-ink-2 truncate" title={t('Payé = OpenRouter + Claude hors crédit')}>{t('payé {x} DH', { x: money(paidMad, lang) })}</div>
      )}
    </div>
  );
}

function DayChart({ days, lang }: { days: AiCostsResponse['period']['byDay']; lang: string }) {
  const { t } = useT();
  const W = 600; const H = 110;
  const max = Math.max(1, ...days.map((d) => d.mad ?? 0));
  const bw = W / Math.max(1, days.length);
  const short = (day: string) => new Date(`${day}T12:00:00Z`).toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' }).replace('.', '');
  const total = days.reduce((s, d) => s + (d.mad ?? 0), 0);
  const summary = t('{n} jours : {x} DH de coût IA', { n: days.length, x: formatNum(total) });
  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="block w-full h-[110px]" role="img" aria-label={summary}>
        <title>{summary}</title>
        {days.map((d, i) => {
          const h = d.mad == null ? 0 : (d.mad / max) * (H - 6);
          const paid = d.mad != null && paidDiffers(d.mad, d.paidMad) ? ` · ${t('payé {x} DH', { x: money(d.paidMad, lang) })}` : '';
          return (
            <rect key={d.day} x={i * bw + bw * 0.15} y={H - h} width={bw * 0.7} height={Math.max(d.mad ? 1 : 0, h)} rx={2} fill={GOLD} fillOpacity={i === days.length - 1 ? 1 : 0.55}>
              <title>{`${short(d.day)} · ${d.mad == null ? t('données manquantes') : `${money(d.mad, lang)} DH`}${paid}`}</title>
            </rect>
          );
        })}
        <line x1={0} y1={H - 0.5} x2={W} y2={H - 0.5} style={{ stroke: 'var(--color-adm-line)' }} vectorEffect="non-scaling-stroke" />
      </svg>
      {days.length > 0 && (
        <div className="flex justify-between text-[10px] text-adm-faint mt-0.5">
          <span>{short(days[0].day)}</span><span>{short(days[days.length - 1].day)}</span>
        </div>
      )}
    </div>
  );
}

function Breakdown({ title, rows, total, label, lang }: { title: string; rows: AiCostRow[]; total: number; label: (s: string) => string; lang: string }) {
  const { t } = useT();
  return (
    <div className="min-w-0">
      <h3 className="text-[12.5px] font-bold text-adm-ink mb-1.5 m-0">{title}</h3>
      {rows.length === 0 ? <p className="text-[12px] text-adm-muted">{t('Aucun appel sur la période.')}</p> : (
        <ul className="space-y-1">
          {rows.slice(0, 10).map((r) => (
            <li key={r.name} className="grid grid-cols-[1fr_auto] gap-2 text-[12.5px] items-center">
              <span className="min-w-0">
                <span className="block truncate text-adm-ink-2" title={r.name}>{label(r.name)}</span>
                <span className="block h-[4px] rounded bg-adm-surface-3 mt-0.5 overflow-hidden">
                  <span className="block h-full rounded" style={{ width: `${total > 0 ? Math.max(2, (r.mad / total) * 100) : 0}%`, background: GOLD }} />
                </span>
              </span>
              <span className="text-right tabular-nums whitespace-nowrap text-adm-ink">
                {money(r.mad, lang)} DH <small className="text-adm-muted">· {formatNum(r.calls)}</small>
                {paidDiffers(r.mad, r.paidMad) && <small data-ai-paid className="block text-adm-muted">{t('payé {x} DH', { x: money(r.paidMad, lang) })}</small>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function GapLine({ gap }: { gap: AiCostsResponse['gap'] }) {
  const { t } = useT();
  const last = gap?.days?.[0];
  if (!gap || !last) {
    return <p className="text-[12px] text-adm-muted">{t('Vérification OpenRouter : pas encore de journée complète à comparer.')}</p>;
  }
  if (gap.flagged) {
    return (
      <p data-ai-gap className="text-[12.5px] font-semibold text-status-waiting">
        {t('Coût IA : écart {x} % avec OpenRouter', { x: Math.round(Math.abs(gap.flagged.gapPct)) })}
        <span className="font-normal text-adm-muted"> · {frDate(gap.flagged.day)} · {t('clé')} {gap.flagged.key}</span>
      </p>
    );
  }
  return (
    <p className="text-[12.5px] text-status-done font-semibold">
      ✓ {t('Conforme à OpenRouter')}
      <span className="font-normal text-adm-muted"> · {frDate(last.day)} · {last.gapPct == null ? '—' : t('écart {x} %', { x: Math.abs(last.gapPct) })}</span>
    </p>
  );
}

/** The daily Claude check (ai_claude_checks, written by another job): amber when flagged. */
function ClaudeGapLine({ check }: { check: AiClaudeCheck | null | undefined }) {
  const { t } = useT();
  if (check === undefined) return null; // an older worker
  if (!check) return <p className="text-[12px] text-adm-muted">{t('Vérification Claude : pas encore de comparaison.')}</p>;
  if (check.flagged) {
    return (
      <p data-ai-claude-gap className="text-[12.5px] font-semibold text-status-waiting">
        {t('Coût IA : écart {x} % avec Claude', { x: check.gapPct == null ? '—' : Math.round(Math.abs(check.gapPct)) })}
        <span className="font-normal text-adm-muted"> · {frDate(check.day)} · {check.source === 'console' ? t('montant console') : t('API Anthropic')}</span>
      </p>
    );
  }
  return (
    <p className="text-[12.5px] text-status-done font-semibold">
      ✓ {t('Conforme à Claude')}
      <span className="font-normal text-adm-muted"> · {frDate(check.day)} · {check.gapPct == null ? '—' : t('écart {x} %', { x: Math.abs(check.gapPct) })}</span>
    </p>
  );
}

/** OpenRouter / Claude, réel and payé, and Claude's credit used vs paid. */
function Providers({ rows, claude, lang }: { rows: AiCostRow[]; claude: NonNullable<AiCostsResponse['period']['claude']> | undefined; lang: string }) {
  const { t } = useT();
  return (
    <div className="min-w-0" data-ai-providers>
      <h3 className="text-[12.5px] font-bold text-adm-ink mb-1.5 m-0">{t('Par fournisseur')}</h3>
      {rows.length === 0 ? <p className="text-[12px] text-adm-muted">{t('Aucun appel sur la période.')}</p> : (
        <ul className="space-y-1">
          {rows.map((r) => (
            <li key={r.name} className="grid grid-cols-[1fr_auto] gap-2 text-[12.5px] items-baseline">
              <span className="truncate text-adm-ink-2">{PROVIDER_LABEL[r.name] ?? r.name} <small className="text-adm-muted">· {t('{n} appels', { n: formatNum(r.calls) })}</small></span>
              <span className="text-right tabular-nums whitespace-nowrap text-adm-ink">
                {t('réel {x} DH', { x: money(r.mad, lang) })}
                <small className="text-adm-muted"> · {t('payé {x} DH', { x: money(r.paidMad ?? r.mad, lang) })}</small>
              </span>
            </li>
          ))}
        </ul>
      )}
      {claude && claude.calls > 0 && (
        <p data-ai-credit className="text-[12px] text-adm-ink-2 mt-1.5">
          {t('Crédit Claude : {a} DH utilisés · {b} DH payés', { a: money(claude.coveredMad, lang), b: money(claude.paidMad, lang) })}
        </p>
      )}
    </div>
  );
}

/**
 * « En pause » (owner, 2026-11-02): tasks the loop alarm stopped by itself, one
 * line each, with « Reprendre » once the cause is fixed. Nothing paused → nothing shown.
 */
function AiPauses() {
  const { t } = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['ai-pauses'] as const, queryFn: () => dashboardApi.aiPauses(), staleTime: 30_000 });
  const resume = useMutation({
    mutationFn: (id: string) => dashboardApi.resumeAiPause(id),
    onSettled: () => qc.invalidateQueries({ queryKey: ['ai-pauses'] }),
  });
  const rows = q.data?.pauses ?? [];
  if (!rows.length) return null;
  return (
    <div data-ai-pauses className="rounded-[14px] border border-status-waiting/40 bg-status-waiting/10 px-4 py-3">
      <h3 className="text-[13px] font-bold text-adm-ink m-0 mb-1.5">{t('Mis en pause automatiquement')}</h3>
      <ul className="space-y-1.5">
        {rows.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px]">
            <span className="text-adm-ink font-semibold">{aiFeatureLabel(r.feature)}</span>
            <span className="text-adm-ink-2">
              {r.shopifyOrderId ? <Link to={`/admin/orders/${r.shopifyOrderId}`} className="underline">{r.orderName ?? r.shopifyOrderId}</Link> : (r.orderName ?? r.chat_id ?? '—')}
            </span>
            <span className="text-adm-muted">{t('{n} appels en 5 min ({f} en erreur)', { n: r.calls ?? 0, f: r.failed ?? 0 })}</span>
            <button
              type="button"
              disabled={resume.isPending}
              onClick={() => resume.mutate(r.id)}
              className="ml-auto rounded-full border border-adm-ink/20 bg-white px-3 py-1 text-[12px] font-semibold text-adm-ink hover:bg-adm-surface-3 disabled:opacity-50"
            >{t('Reprendre')}</button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AiCostsCard({ range }: { range: DateRange }) {
  const { t, lang } = useT();
  const q = useQuery({
    queryKey: ['ai-costs', range.from ?? null, range.to ?? null] as const,
    queryFn: () => dashboardApi.aiCosts(range),
    staleTime: 60_000,
  });
  const d = q.data;
  if (q.error) return <p className="text-sm text-status-stuck mb-7">{t('Chiffres indisponibles — aucun chiffre plutôt qu’un chiffre faux.')}</p>;
  if (!d) return <div aria-busy className="h-[260px] rounded-xl bg-adm-surface-3 animate-pulse mb-7" />;
  const p = d.period;
  return (
    <div className="mb-7 space-y-3">
      <AiPauses />
      <div className="grid grid-cols-3 gap-2">
        <Tile label={t("Aujourd'hui")} {...d.totals.today} lang={lang} />
        <Tile label={t('7 jours')} {...d.totals.d7} lang={lang} />
        <Tile label={t('Mois')} {...d.totals.month} lang={lang} />
      </div>

      <div className={`bg-white rounded-[14px] border ${CARD_BORDER} px-4 py-3.5 space-y-3`}>
        <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
          <div>
            <span className="text-[11.5px] text-adm-muted font-medium">{t('Sur la période')} </span>
            <b className="text-[18px] text-adm-ink" style={{ fontFamily: HEADING_FONT }}>{p.missing ? '—' : `${money(p.mad, lang)} DH`}</b>
            {!p.missing && p.paidMad != null && (
              <span data-ai-period-paid className="text-[12px] text-adm-ink-2" title={t('Réel = valeur de tous les appels · Payé = OpenRouter + Claude hors crédit')}>
                {' '}{t('réel')} · <b>{t('payé {x} DH', { x: money(p.paidMad, lang) })}</b>
              </span>
            )}
            <span className="text-[12px] text-adm-muted"> · {t('{n} appels', { n: formatNum(p.calls) })}{p.partial ? ` · ${t('partiel')}` : ''}</span>
            {p.missing && <span className="text-[12px] text-status-waiting font-semibold"> · {t('données manquantes')}{p.reason ? ` (${p.reason})` : ''}</span>}
          </div>
          {p.perDeliveredOrder && (
            <div className="text-[12.5px] text-adm-ink-2">
              {t('Par commande livrée')} : <b>{p.perDeliveredOrder.avg == null ? '—' : `${money(p.perDeliveredOrder.avg, lang)} DH`}</b>
              <span className="text-adm-muted"> · {t('{n} commandes', { n: p.perDeliveredOrder.orders })}</span>
            </div>
          )}
          <div className="sm:ml-auto space-y-0.5"><GapLine gap={d.gap} /><ClaudeGapLine check={d.claudeCheck} /></div>
        </div>

        <DayChart days={p.byDay} lang={lang} />

        {p.byProvider && <Providers rows={p.byProvider} claude={p.claude} lang={lang} />}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Breakdown title={t('Par fonction')} rows={p.byFeature} total={p.mad} label={(s) => t(aiFeatureLabel(s))} lang={lang} />
          <Breakdown title={t('Par modèle')} rows={p.byModel} total={p.mad} label={(s) => s.replace(/^[^/]+\//, '')} lang={lang} />
          <div className="min-w-0">
            <h3 className="text-[12.5px] font-bold text-adm-ink mb-1.5 m-0">{t('Commandes les plus coûteuses')}</h3>
            {p.topOrders.length === 0 ? <p className="text-[12px] text-adm-muted">{t('Aucune commande sur la période.')}</p> : (
              <ol className="space-y-1">
                {p.topOrders.map((o) => (
                  <li key={o.orderId} className="grid grid-cols-[1fr_auto] gap-2 text-[12.5px]">
                    {o.shopifyOrderId
                      ? <Link to={`/admin/orders/${o.shopifyOrderId}`} className="truncate text-adm-gold-ink hover:underline">{o.name ?? o.shopifyOrderId}{o.customer ? ` · ${o.customer}` : ''}</Link>
                      : <span className="truncate text-adm-ink-2">{o.name ?? o.orderId}</span>}
                    <span className="text-right tabular-nums text-adm-ink whitespace-nowrap">
                      {money(o.mad, lang)} DH <small className="text-adm-muted">· {o.calls}</small>
                      {paidDiffers(o.mad, o.paidMad) && <small data-ai-paid className="block text-adm-muted">{t('payé {x} DH', { x: money(o.paidMad, lang) })}</small>}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        <p className="text-[11px] text-adm-muted">
          {d.trackingSince ? t('Suivi depuis le {d}', { d: frDate(d.trackingSince) }) : t('Suivi pas encore démarré')}
          {d.completeSince ? ` · ${t('toutes les fonctions depuis le {d}', { d: frDate(d.completeSince) })}` : ''}
          {` · 1 $ = ${formatDecimal(d.fxUsd, lang)} DH`}
          {p.pendingCost > 0 ? ` · ${t('{n} appels en attente de leur prix', { n: p.pendingCost })}` : ''}
        </p>
      </div>
    </div>
  );
}
